import React, { useEffect, useMemo, useRef, useState } from 'react';

import { stringToColor } from '../common/colorUtils.js';
import { getImageThumbnailMedium } from '../../../../lib/imageUtilsESM.js';
import {
  pca2dFromVectors,
  discriminant2dFromVectors,
  nearestNeighborEdges,
  similarityColor,
  similarityBandIndex,
  SIMILARITY_BANDS,
} from './embeddingExplorerUtils.js';

// Fallback SVG user-space size used until the plot area has been measured. Once measured, the
// viewBox matches the plot cell's pixel size 1:1 so the scatter fills the available space exactly
// (no letterboxing) and point/label sizes stay constant regardless of the container's shape.
const FALLBACK_W = 1000;
const FALLBACK_H = 620;

/** Maps PCA coords into a [0,w] x [0,h] box with a small padding margin. */
function scalePointsToSvg(coords, w, h) {
  if (!coords.length) return [];
  const xs = coords.map(p => p[0]);
  const ys = coords.map(p => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const rx = maxX - minX || 1;
  const ry = maxY - minY || 1;
  const pad = 0.06;
  return coords.map(([x, y]) => {
    const nx = pad + (1 - 2 * pad) * ((x - minX) / rx);
    const ny = pad + (1 - 2 * pad) * ((maxY - y) / ry);
    return [nx * w, ny * h];
  });
}

/** Default point color: hashed by stored cluster id (gray when unclustered). */
function defaultBaseColor(item) {
  return item.clusterId ? stringToColor(item.clusterId, { white: 0.5 }) : '#6c757d';
}

function secondaryColor(item) {
  return item.clusterId ? stringToColor(item.clusterId + item.clusterId, { white: 0.3 }) : '#6c757d';
}

/** Converts a client (mouse) position into SVG user-space coordinates, robust to scaling/letterboxing. */
function clientToSvg(svg, clientX, clientY) {
  if (!svg || typeof svg.getScreenCTM !== 'function') return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const p = pt.matrixTransform(ctm.inverse());
  return [p.x, p.y];
}

/** Index of the closest point to (x, y) within `maxDist` user units, or null. */
function nearestWithin(scaled, x, y, maxDist) {
  let best = null;
  let bestD = maxDist * maxDist;
  for (let i = 0; i < scaled.length; i++) {
    const dx = scaled[i][0] - x;
    const dy = scaled[i][1] - y;
    const d = dx * dx + dy * dy;
    if (d <= bestD) { bestD = d; best = i; }
  }
  return best;
}

/** Tracks the pixel size of `ref`'s element, live via ResizeObserver (falling back to a one-shot measure). */
function useElementSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    };
    if (typeof ResizeObserver === 'undefined') {
      measure();
      return undefined;
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/**
 * Isolated, reusable PCA 2D scatter of image embeddings. Presentational + interaction only:
 * the host supplies semantics through callbacks (point color, shape, neighbor highlight, single
 * selection) so the same component serves the embedding explorer and the cluster gallery.
 *
 * Layout: a CSS grid of two rows — the controls row (auto height) and the plot (fills the rest).
 * The whole component fills its parent by default; pass `width`/`height` (px) to fix its size. The
 * plot renders in its measured pixel space, so it fills whatever space it's given without distortion.
 *
 * Two interaction modes:
 *  - Default: clicking a point calls `onPointClick`; clicking the background calls `onBackgroundClick`.
 *  - `selectMode`: drag a rubber-band rectangle to select the enclosed images (replaces the selection).
 *    Holding Ctrl/Cmd refines instead — a Ctrl+drag adds the enclosed images to the current selection,
 *    and a Ctrl+click toggles a single image. Selection is reported as a Set of imgId via `onSelectionChange`.
 *
 * Discriminant view: when `enableDiscriminant` is set, the controls row gains a "Discriminant view"
 * checkbox. Toggling it on swaps PCA for the Fisher discriminant projection between the two clusters
 * present in `items` (x = difference-of-means axis = max separation, y = top orthogonal PCA), and
 * shows the in-sample Fisher J plus held-out AUC. It needs EXACTLY two distinct `clusterId`s (or two
 * distinct values from `discriminantLabelForItem`); otherwise it falls back to PCA and says so.
 *
 * @param {Object} props
 * @param {Array<{imgId:string, imgUrl:string, vector:number[], clusterId?:string, clusterIdx?:number, crossCluster?:boolean}>} props.items
 * @param {[number,number][]} [props.coords]  Precomputed PCA coords (else computed from `items` vectors).
 * @param {boolean} [props.enableDiscriminant]  Show a discriminant-view toggle (see above).
 * @param {(item: Object, i: number) => *} [props.discriminantLabelForItem]  Group label per item for the
 *   discriminant projection (default: `item.clusterId`). The two distinct values define the two clusters.
 * @param {number}  [props.width]             Fixed width in px (else fills parent).
 * @param {number}  [props.height]            Fixed height in px (else fills parent).
 * @param {string}  [props.title]             Text shown in the controls row.
 * @param {number}  [props.selectedIndex]     Index (into `items`) of the single-selected point.
 * @param {Map<number, {sim:number, rank?:number}>} [props.neighborByIndex]  Per-index neighbor info; colors those points by similarity.
 * @param {(item: Object, i: number) => string} [props.baseColorForItem]  Fill color for non-selected/non-neighbor points.
 * @param {(item: Object, i: number) => ('circle'|'square')} [props.shapeForItem]  Point shape (default circle).
 * @param {boolean} [props.crossClusterBorder]  Draw a black border on cross-cluster points: those
 *   flagged `item.crossCluster`, and (when `markClosestCrossCluster`) those whose single nearest
 *   neighbour over all `items` belongs to a different `clusterId`.
 * @param {boolean} [props.markClosestCrossCluster]  Compute the cross-cluster flag from the data:
 *   mark a point when its closest image is in another cluster. Needs `clusterId` on the items.
 * @param {(item: Object, i: number, e: MouseEvent) => void} [props.onPointClick]
 * @param {() => void} [props.onBackgroundClick]
 * @param {Array<{from:number, to:number, sim:number}>} [props.nnEdges]  Precomputed NN edges (else computed from `items`).
 * @param {boolean} [props.showNnEdges]        Controlled NN-edge visibility (else internal state).
 * @param {boolean} [props.defaultShowNnEdges] Initial NN-edge visibility when uncontrolled.
 * @param {(show: boolean) => void} [props.onToggleNnEdges]  Toggle handler when `showNnEdges` is controlled.
 * @param {boolean} [props.selectMode]         Rubber-band multi-selection mode (see above).
 * @param {Set<string>} [props.selectedKeys]   Multi-selected imgIds (controlled, with `onSelectionChange`).
 * @param {(keys: Set<string>) => void} [props.onSelectionChange]
 * @param {Set<string>} [props.highlightKeys]  imgIds to ring-highlight on top of everything else
 *   (transient emphasis driven from elsewhere, e.g. hovering a pair in a sibling view).
 */
export default function ImagePCAScatterPlot({
  items,
  coords: coordsProp = null,
  width = null,
  height = null,
  title = 'PCA 2D (Gram / dual PCA on X Xᵀ).',
  // discriminant (Fisher max-separation) projection between the two clusters present in `items`
  enableDiscriminant = false,
  discriminantLabelForItem = null,
  // single-selection / neighbor overlay (embedding explorer)
  selectedIndex = null,
  neighborByIndex = null,
  baseColorForItem = defaultBaseColor,
  shapeForItem = null,
  crossClusterBorder = true,
  markClosestCrossCluster = false,
  onPointClick = null,
  onBackgroundClick = null,
  // nearest-neighbor edges
  nnEdges: nnEdgesProp = null,
  showNnEdges: showNnEdgesProp = null,
  defaultShowNnEdges = true,
  onToggleNnEdges = null,
  // rectangle multi-selection (cluster gallery)
  selectMode = false,
  selectedKeys = null,
  onSelectionChange = null,
  // transient ring-highlight of specific imgIds, driven by a sibling view
  highlightKeys = null,
}) {
  const svgRef = useRef(null);
  const plotRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [uncontrolledShowEdges, setUncontrolledShowEdges] = useState(defaultShowNnEdges);
  // When on (and enabled), swap PCA for the Fisher discriminant projection between the two clusters.
  const [discriminant, setDiscriminant] = useState(!!enableDiscriminant);
  // drag = { x0, y0, x1, y1, additive, baseline:Set<imgId>, moved:boolean } in SVG user space.
  const [drag, setDrag] = useState(null);

  // The plot renders in the measured pixel size of its cell, so it fills whatever space it's given.
  const { w: measuredW, h: measuredH } = useElementSize(plotRef);
  const W = measuredW || FALLBACK_W;
  const H = measuredH || FALLBACK_H;

  const showNnEdges = showNnEdgesProp != null ? showNnEdgesProp : uncontrolledShowEdges;
  const setShowNnEdges = onToggleNnEdges || setUncontrolledShowEdges;

  // Discriminant projection (x = the Fisher difference-of-means axis between the two clusters, the
  // direction of greatest separation; y = top orthogonal PCA). Computed only while the view is on.
  // Also carries fisherJ / aucHeldOut / fallback for the controls-row readout.
  const disc = useMemo(() => {
    if (!enableDiscriminant || !discriminant || !items.length) return null;
    const labelOf = discriminantLabelForItem || ((it) => it.clusterId);
    return discriminant2dFromVectors(items.map(it => it.vector), items.map(labelOf));
  }, [enableDiscriminant, discriminant, items, discriminantLabelForItem]);

  const coords = useMemo(
    () => (disc ? disc.coords : coordsProp || pca2dFromVectors(items.map(it => it.vector)).coords),
    [disc, coordsProp, items],
  );
  const scaled = useMemo(() => scalePointsToSvg(coords, W, H), [coords, W, H]);
  // While the (non-fallback) discriminant view is active, override the caller's title with the axis legend.
  const effectiveTitle = disc && !disc.fallback
    ? 'Discriminant axis → x = max cluster separation (Fisher); y = top orthogonal PCA.'
    : title;
  const computedEdges = useMemo(
    () => (showNnEdges && !nnEdgesProp ? nearestNeighborEdges(items) : []),
    [showNnEdges, nnEdgesProp, items],
  );
  const edges = showNnEdges ? (nnEdgesProp || computedEdges) : [];

  // Indices whose single nearest neighbour (over all items) sits in a DIFFERENT clusterId — i.e.
  // the point's closest image is in the other cluster. Reuses precomputed nnEdges when available
  // (else computes them once here, regardless of edge visibility). Drives the crossCluster border.
  const closestCrossClusterSet = useMemo(() => {
    if (!markClosestCrossCluster) return null;
    const set = new Set();
    for (const e of nnEdgesProp || nearestNeighborEdges(items)) {
      const from = items[e.from];
      const to = items[e.to];
      if (from && to && from.clusterId != null && to.clusterId != null
        && String(from.clusterId) !== String(to.clusterId)) {
        set.add(e.from);
      }
    }
    return set;
  }, [markClosestCrossCluster, nnEdgesProp, items]);

  // --- rectangle / ctrl-click selection (selectMode only) -------------------
  const beginDrag = (e) => {
    if (!selectMode) return;
    const p = clientToSvg(svgRef.current, e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    // Capture the pointer so the drag keeps tracking (and ends cleanly) even if
    // the mouse leaves the SVG before the button is released.
    svgRef.current.setPointerCapture?.(e.pointerId);
    setDrag({
      x0: p[0], y0: p[1], x1: p[0], y1: p[1],
      additive: e.ctrlKey || e.metaKey,
      baseline: new Set(selectedKeys || []),
      moved: false,
    });
  };

  const updateDrag = (e) => {
    if (!drag) return;
    const p = clientToSvg(svgRef.current, e.clientX, e.clientY);
    if (!p) return;
    setDrag(d => (d ? {
      ...d,
      x1: p[0],
      y1: p[1],
      moved: d.moved || Math.hypot(p[0] - d.x0, p[1] - d.y0) > 6,
    } : d));
  };

  const finishDrag = (e) => {
    if (!drag) return;
    if (e && svgRef.current?.hasPointerCapture?.(e.pointerId)) {
      svgRef.current.releasePointerCapture(e.pointerId);
    }
    const { x0, y0, x1, y1, additive, baseline, moved } = drag;
    let next;
    if (moved) {
      // Rubber-band: everything inside the rectangle.
      const minX = Math.min(x0, x1);
      const maxX = Math.max(x0, x1);
      const minY = Math.min(y0, y1);
      const maxY = Math.max(y0, y1);
      next = additive ? new Set(baseline) : new Set();
      scaled.forEach((pt, i) => {
        if (pt[0] >= minX && pt[0] <= maxX && pt[1] >= minY && pt[1] <= maxY) next.add(items[i].imgId);
      });
    } else {
      // Click (no drag): Ctrl toggles a single point, a plain click selects just it (or clears).
      const hit = nearestWithin(scaled, x0, y0, 16);
      if (additive) {
        next = new Set(baseline);
        if (hit != null) {
          const key = items[hit].imgId;
          if (next.has(key)) next.delete(key); else next.add(key);
        }
      } else {
        next = new Set();
        if (hit != null) next.add(items[hit].imgId);
      }
    }
    setDrag(null);
    onSelectionChange?.(next);
  };

  // --- per-point draw order: plain → neighbors (rank 10→1) → selected on top -
  const orderedIndices = [];
  for (let i = 0; i < scaled.length; i++) {
    if (i !== selectedIndex && !(neighborByIndex && neighborByIndex.has(i))) orderedIndices.push(i);
  }
  if (neighborByIndex) {
    Array.from(neighborByIndex.entries())
      .sort((a, b) => b[1].rank - a[1].rank)
      .forEach(([i]) => orderedIndices.push(i));
  }
  if (selectedIndex != null && selectedIndex < scaled.length) orderedIndices.push(selectedIndex);

  const dragRect = drag && drag.moved ? {
    x: Math.min(drag.x0, drag.x1),
    y: Math.min(drag.y0, drag.y1),
    width: Math.abs(drag.x1 - drag.x0),
    height: Math.abs(drag.y1 - drag.y0),
  } : null;

  // Render the plot only once its cell has been measured, so the viewBox matches the pixel size
  // from the first painted frame (no transient distortion against the fallback size).
  const measured = measuredW > 0 && measuredH > 0;
  const hasPoints = scaled.length > 0 && measured;

  return (
    <div
      className="scatter-wrap"
      style={{
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr)',
        width: width != null ? `${width}px` : '100%',
        height: height != null ? `${height}px` : '100%',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div className="scatter-controls d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
        <label
          className="small text-muted d-inline-flex align-items-center mb-1 ml-1"
          style={{ gap: '4px', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={showNnEdges}
            onChange={e => setShowNnEdges(e.target.checked)}
          />
          Show most sim. edge
        </label>
        {enableDiscriminant ? (
          <>
            <label
              className="small text-muted d-inline-flex align-items-center mb-1"
              style={{ gap: '4px', cursor: 'pointer' }}
              title="Swap the variance-maximizing PCA for the discriminant projection: x is the difference-of-means axis between the two clusters (the direction of greatest separation), y is the top orthogonal PCA."
            >
              <input type="checkbox" checked={discriminant} onChange={e => setDiscriminant(e.target.checked)} />
              Max 2 cluster separation
            </label>
            {disc ? (
              disc.fallback ? (
                <span className="small text-muted mb-1">discriminant needs exactly two clusters</span>
              ) : (
                <span className="small text-muted mb-1">
                  <span title="In-sample Fisher ratio: squared mean gap over within-cluster variance on the separation axis. Higher = better separated (optimistic — fit to these same points).">
                    Fisher J <strong>{disc.fisherJ.toFixed(2)}</strong>
                  </span>
                  {disc.aucHeldOut != null ? (
                    <span className="ml-2" title="Held-out AUC: axis fit on half the images, scored on the other half. ≈0.50 = indistinguishable (effectively the same cluster); ≈1.0 = genuinely distinct.">
                      · held-out AUC <strong>{disc.aucHeldOut.toFixed(2)}</strong>
                    </span>
                  ) : null}
                </span>
              )
            ) : null}
          </>
        ) : null}
      </div>

      <div ref={plotRef} style={{ minHeight: 0, minWidth: 0 }}>
        {hasPoints ? (
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            height="100%"
            style={{ display: 'block', cursor: selectMode ? 'crosshair' : 'default', userSelect: 'none' }}
            preserveAspectRatio="none"
            onPointerDown={beginDrag}
            onPointerMove={selectMode ? updateDrag : undefined}
            onPointerUp={selectMode ? finishDrag : undefined}
            onPointerCancel={selectMode ? finishDrag : undefined}
          >
            <defs>
              {SIMILARITY_BANDS.map((band, i) => (
                <marker
                  key={i}
                  id={`nn-arrow-${i}`}
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="4"
                  markerHeight="4"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={band.color} />
                </marker>
              ))}
            </defs>
            <rect
              x="0"
              y="0"
              width={W}
              height={H}
              fill="#fafafa"
              onClick={selectMode ? undefined : onBackgroundClick}
            />
            <text x="16" y="24" fontSize="14" fill="#333">
              {effectiveTitle}{selectMode ? ' Drag to select' : ' Click a point to select'}
            </text>
            {edges.flatMap((e, k) => {
              const a = scaled[e.from];
              const b = scaled[e.to];
              if (!a || !b) return null;
              const dx = b[0] - a[0];
              const dy = b[1] - a[1];
              const len = Math.hypot(dx, dy) || 1;
              const ux = dx / len;
              const uy = dy / len;
              const x1 = a[0] + ux * 5;
              const y1 = a[1] + uy * 5;
              const x2 = b[0] - ux * 7;
              const y2 = b[1] - uy * 7;
              const bi = similarityBandIndex(e.sim);
              return [
                <line
                  key={`nn-${k}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={SIMILARITY_BANDS[bi].color}
                  strokeWidth={1}
                  opacity={0.65}
                  markerEnd={`url(#nn-arrow-${bi})`}
                  style={{ pointerEvents: 'none' }}
                />,
                ... (hoverIdx === e.from ? [
                  <text x={(x1 + x2) / 2} y={(y1 + y2) / 2} textAnchor="middle" fontSize="9" fill="#111" style={{ pointerEvents: 'none' }}>
                    {e.sim.toFixed(4)}
                  </text>,                  
                ] : []),
              ];
            })}
            {orderedIndices.map(i => {
              const pt = scaled[i];
              const item = items[i];
              const isSelected = selectedIndex === i;
              const neighbor = neighborByIndex ? neighborByIndex.get(i) : null;
              const isMultiSelected = selectMode && selectedKeys && selectedKeys.has(item.imgId);

              let fill;
              let secondary = secondaryColor(item);
              if (isSelected) {
                fill = '#0d6efd';
              } else if (neighbor) {
                fill = similarityColor(neighbor.sim);
              } else {
                fill = baseColorForItem(item, i);
              }

              let r = isSelected ? 10 : neighbor ? 8 : (hoverIdx === i ? 10 : 6);
              if (isMultiSelected)
                r = 8;

              if (orderedIndices.length > 100) {
                r = r * 0.7;
              }

              const crossCluster = crossClusterBorder
                && (!!item.crossCluster || (closestCrossClusterSet ? closestCrossClusterSet.has(i) : false));
              const stroke = isMultiSelected ? '#0d6efd' : (crossCluster ? '#000' : secondary);
              const strokeWidth = isMultiSelected ? 3 : (crossCluster ? 2 : 1);

              const props = {
                fill,
                stroke,
                strokeWidth,
                style: { cursor: selectMode ? 'crosshair' : 'pointer' },
                onMouseEnter: () => setHoverIdx(i),
                onMouseLeave: () => setHoverIdx(null),
                onClick: selectMode ? undefined : (e => onPointClick?.(item, i, e)),
              };

              const shape = (shapeForItem ? shapeForItem(item, i) : 'circle');

              return (
                <g key={i}>
                  {shape === 'circle' ? (
                    <circle cx={pt[0]} cy={pt[1]} r={r} {...props} />
                  ) : (
                    <rect x={pt[0] - r} y={pt[1] - r} width={r * 2} height={r * 2} {...props} />
                  )}
                  {neighbor && (
                    <text
                      x={pt[0]}
                      y={pt[1] + 4}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="bold"
                      fill="#111"
                      style={{ pointerEvents: 'none' }}
                    >
                      {neighbor.rank}
                    </text>
                  )}
                  <title>
                    {item.imgUrl}
                    {item.clusterIdx >= 0 ? ` · cluster ${item.clusterIdx}` : ''}
                    {neighbor ? ` (#${neighbor.rank}, sim=${neighbor.sim.toFixed(4)})` : ''}
                    {crossCluster ? ' · closest image is in the other cluster' : ''}
                  </title>
                </g>
              );
            })}
            {highlightKeys && highlightKeys.size > 0 && scaled.map((pt, i) => (
              highlightKeys.has(items[i].imgId) ? (
                <circle
                  key={`hl-${i}`}
                  cx={pt[0]}
                  cy={pt[1]}
                  r={orderedIndices.length > 100 ? 9 : 12}
                  fill="#ffff0055"
                  stroke="yellow"
                  strokeWidth={1}
                  style={{ pointerEvents: 'none' }}
                />
              ) : null
            ))}
            {neighborByIndex && neighborByIndex.size > 0 && renderSimilarityLegend(H)}
            {dragRect && (
              <rect
                x={dragRect.x}
                y={dragRect.y}
                width={dragRect.width}
                height={dragRect.height}
                fill="#0d6efd22"
                stroke="#0d6efd"
                strokeWidth={1.5}
                strokeDasharray="6 4"
                style={{ pointerEvents: 'none' }}
              />
            )}
            {renderHoverPreview(scaled, items, hoverIdx, W, H)}
          </svg>
        ) : null}
      </div>
    </div>
  );
}

function renderSimilarityLegend(h) {
  const baseX = 16;
  const baseY = h - 24;
  const slotW = 92;
  return (
    <g>
      <text x={baseX} y={baseY - 16} fontSize="12" fill="#333">
        Top 10 similarity [0,1]:
      </text>
      {SIMILARITY_BANDS.map((band, i) => (
        <g key={i} transform={`translate(${baseX + i * slotW}, ${baseY})`}>
          <circle cx={6} cy={6} r={6} fill={band.color} stroke="#fff" strokeWidth={1} />
          <text x={18} y={10} fontSize="11" fill="#333">
            {band.label}
          </text>
        </g>
      ))}
    </g>
  );
}

/** Floating image preview of the hovered point, clamped to stay inside the canvas. */
function renderHoverPreview(scaled, items, hoverIdx, w, h) {
  if (hoverIdx == null || hoverIdx < 0 || hoverIdx >= scaled.length) return null;

  const pt = scaled[hoverIdx];
  const item = items[hoverIdx];
  const margin = 4;
  const imgW = Math.max(80, Math.min(300, w * 0.5));
  const imgH = imgW;

  let x = pt[0] + margin;
  let y = pt[1] - imgH / 2;
  if (x + imgW > w - margin) x = pt[0] - imgW - margin;
  x = Math.max(margin, Math.min(x, w - imgW - margin));
  y = Math.max(margin, Math.min(y, h - imgH - margin));

  return (
    <g pointerEvents="none">
      <rect
        x={x}
        y={y}
        width={imgW}
        height={imgH}
        fill={item.clusterId ? stringToColor(item.clusterId, { white: 0.3 }) : '#fff'}
        stroke="#333"
        strokeWidth="1"
        rx="6"
      />
      <image
        href={getImageThumbnailMedium(item.imgUrl)}
        x={x}
        y={y}
        width={imgW}
        height={imgH}
        preserveAspectRatio="xMidYMid meet"
      />
    </g>
  );
}
